import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useGetDashboardApsQuery } from '../../../api/dashboardApiSlice';
import { Loader } from '../../components';
import FilterButton, { FilterGroup } from '../../FilterButton';
import Pagination from '../../tables/Pagination';
import Table from '../../tables/Table';
import TableSearch from '../../tables/TableSearch';
import useCustomTable from '../../tables/useCustomTable';
import Block from '../../Block'
import { OsIcon } from '../../OsIcon';
import EnvironmentCell from '../../metrics/tables/cells/EnvironmentCell';
import DevicesCell from '../../metrics/tables/cells/DevicesCell';
import OptInRateCell from '../../metrics/tables/cells/OptInRateCell';
import versionCompare from '../../../services/versionCompare';
import { Badge, Button } from 'react-bootstrap';
import {formatNumber} from "../../Format";
import SdkCell from '../../tables/SdkCell';

const Enabled = ({value}) => {
  const { t } = useTranslation();

  if(value) {
    return (<Badge variant="success">{t("stats.acitve")}</Badge>)
  } else {
    return (<Badge variant="danger">{t("stats.inactive")}</Badge>)
  }
}

const AdminDashboardAppTab = ({filter}) => {
  const { t, i18n } = useTranslation();
  const {
    tableProps,
    paginationProps,
    tableFilterProps,
    tableSearchProps
  } = useCustomTable({
    sortable: true,
    paginate: true,
    sortBy: {name: 'asc'},
    initialFilters: [{id: "environment", value: ["production"]}]
  });
  const history = useHistory();
  const { data = [], isSuccess, isLoading, refetch } = useGetDashboardApsQuery({filter})
  const tableData = useMemo(()=>{
    return data.map((row) => {
      return {
        ...row,
        opt_in_geolocated_devices: row.location_authorized_devices/row.active_devices,
        ref_opt_in_geolocated_devices: row.ref_location_authorized_devices/row.ref_active_devices,
        loc_consent_rate: row.loc_consented_devices/row.location_authorized_devices,
        ref_loc_consent_rate: row.ref_loc_consented_devices/row.ref_location_authorized_devices,
        consent_rate: row.location_authorized_devices/row.consented_devices,
        ref_consent_rate: row.ref_location_authorized_devices/row.ref_consented_devices,
      }
    })
  }, [data])

  const formatAppName = (app) => ( app.name ?? app.bundle_id )
  const toFloat = (val) => {
    if (isNaN(val)) { return -1 }
    return parseFloat(val)
  }

  const columns = useMemo(() => [{
    Header: t("publisher.app.name"),
    Cell: ({row}) => (String(formatAppName(row.original))),
    accessor: 'name',
    sortType: (rowA, rowB, id, desc) => (formatAppName(rowB.original).localeCompare(formatAppName(rowA.original)))
  },{
    Header: t("publisher.app.os"),
    Cell: ({row}) => (<OsIcon os={row.original.os} />),
    accessor: 'os',
  },{
    Header: t("publisher.app.environment"),
    Cell: ({row}) => (<EnvironmentCell row={row.original} i18n={i18n} />),
    accessor: 'environment',
  },{
    Header: t("publisher.app.production_sdk"),
    Cell: ({row}) => (<SdkCell sdk={row.original.prod_sdk} i18n={i18n} />),
    accessor: 'production_sdk',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowB.original.prod_sdk.version,rowA.original.prod_sdk.version))
  },{
    Header: t("publisher.app.test_sdk"),
    Cell: ({row}) => (<SdkCell sdk={row.original.test_sdk} i18n={i18n} />),
    accessor: 'test_sdk',
    sortType: (rowA, rowB, id, desc) => (versionCompare(rowB.original.test_sdk.version,rowA.original.test_sdk.version))
  },{
    Header: t("stats.devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.active_devices} refDevices={row.original.ref_active_devices} />),
    accessor: 'active_devices',
  },{
    Header: t("stats.geolocated_devices"),
    Cell: ({row}) => (<DevicesCell devices={row.original.location_authorized_devices} refDevices={row.original.ref_location_authorized_devices} />),
    accessor: 'location_authorized_devices',
  },{
    Header: t("stats.opt_in_geolocated_devices"),
    Cell: ({row}) => (
        <OptInRateCell val={row.original.opt_in_geolocated_devices} refVal={row.original.ref_opt_in_geolocated_devices} />
      ),
    accessor: 'opt_in_geolocated_devices',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.opt_in_geolocated_devices) - toFloat(rowA.original.opt_in_geolocated_devices)))
  },{
    Header: t("stats.consent"),
    Cell: ({row}) => (<DevicesCell devices={row.original.loc_consented_devices} refDevices={row.original.ref_loc_consented_devices} />),
    accessor: 'loc_consented_devices',
  },{
    Header: t("stats.consent_rate"),
    Cell: ({row}) => (<OptInRateCell val={row.original.loc_consent_rate} refVal={row.original.ref_loc_consent_rate} />),
    accessor: 'loc_consent_rate',
    sortType: (rowA, rowB, id, desc) => (Math.sign(toFloat(rowB.original.loc_consent_rate) - toFloat(rowA.original.loc_consent_rate)))
  },{
    Header: t("stats.prev_locations_uu"),
    Cell: ({row}) => (<DevicesCell  devices={row.original.prev_locations} refDevices={row.original.prev_locations}  />),
    accessor: 'prev_locations',
  },{
    Header: t("stats.locations_dau"),
    Cell: ({row}) => (<>{formatNumber(parseFloat(row.original.locations_dau))}</>),
    accessor: 'locations_dau',
  },{
    Header: t("stats.locations_uu_30"),
    Cell: ({row}) => (<DevicesCell  devices={row.original.locations_uu_30_days} refDevices={row.original.ref_locations_uu_30_days}  />),
    accessor: 'locations_uu_30_days',
  },{
    Header: t("publisher.app.cmp"),
    accessor: 'cmp_provider_name'
  },{
    Header: t("publisher.enabled"),
    Cell: ({row}) => (<Enabled  value={row.original.enabled} />),
    accessor: 'enabled',
  }]);

  const showApp = (rowData) => history.push(`/apps/${rowData.slug}`)

  return ( <>
    <FilterGroup {...tableFilterProps}>
      <FilterButton
          title={t("publisher.environment")}
          options={["created", "test", "production"]}
          selected={["production"]}
          search={false}
          includeAll={true}
          name="environment"/>
      <TableSearch {...tableSearchProps} style={{float: "right"}}/>
    </FilterGroup>
    <Block className="mb-0">
      <Loader success={!isLoading && isSuccess} error={!isLoading && !isSuccess} retry={refetch}>
        <Table
          noPadding
          data={tableData}
          columns={columns}
          {...tableProps}
          onRowClick={showApp} />
      </Loader>
    </Block>
    <Pagination {...paginationProps}/>
  </> );
}

export default AdminDashboardAppTab;